import { useEffect, useRef, type RefObject } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useSpeakingState, type SpeakingPose } from './SpeakingContext.js';

/**
 * The voice float — the voice-activity levitation on cast tiles
 * (docs/briefs/portrait-system.md §6, "S2 séance", locked). The speaker's
 * tile lifts off the page on a damped spring and floats with a faint seeded
 * wobble; the warm drop-shadow beneath deepens with the slow amplitude.
 * Speaking is neither consequence nor otherworldly, so the treatment uses
 * NEITHER accent: its media are elevation, light/shadow, and motion only.
 *
 * One module-level rAF loop integrates every airborne tile and goes fully
 * idle (no scheduled frame) when nothing is speaking or moving. The engine
 * writes four custom properties + a data attribute; AvatarFrame.css owns the
 * transform/filter that consume them:
 *   --float-y   normalized altitude, 0 … -1 of the tile's max lift
 *                (CSS scales by --susdle-seat-size so mobile lifts less)
 *   --float-amp slow-averaged amplitude 0…1 (scale swell + shadow depth)
 *   --float-wx / --float-wy  wobble angles (deg), gated by --float-sw
 *   --float-sw  altitude gate 0…1 — rotation fades out near the page so
 *                the card always lands level
 *
 * Discord only ever reports binary speaking on/off — no loudness. The
 * envelope below (seeded AMPSEQ walk + burst-length ramp) is choreography,
 * and the design owns that honestly. All numbers are the brief's confirmed
 * tuning JSON; they are provisional by design until the live-call
 * calibration pass that follows the Discord wiring plan.
 */

interface SeanceTuning {
  /** Amplitude floor held between closely-spaced bursts (word gaps dip, never land). */
  readonly hover: number;
  /** ms of silence before the card is released to glide home. */
  readonly release: number;
  /** ms — shadow transition when landing (CSS; the spring owns height). */
  readonly settle: number;
  /** ms — shadow transition on lift-off (CSS). */
  readonly attack: number;
  /** Fast low-pass factor per tick (per-word texture). */
  readonly fast: number;
  /** Slow low-pass factor per tick (the swell consumers read). */
  readonly slow: number;
  /** Envelope tick, ms. */
  readonly tick: number;
  /** Global lift multiplier on the 14px/104px-tile maximum. */
  readonly lift: number;
  /** Global wobble multiplier on the ±4°/±5° sines. */
  readonly wobble: number;
  /** Spring stiffness (height integration). */
  readonly springK: number;
  /** Damping ratio — 0.85 gives a faint organic overshoot. */
  readonly springDamp: number;
}

/** Confirmed tuning (probe knob JSON, 2026-06-12). */
export const VOICE_FLOAT_TUNING: SeanceTuning = {
  hover: 0.3,
  release: 380,
  settle: 560,
  attack: 320,
  fast: 0.3,
  slow: 0.12,
  tick: 140,
  lift: 0.85,
  wobble: 0.7,
  springK: 135,
  springDamp: 0.85,
};

/** Stepped pseudo-amplitude sequence each speaker walks from a seeded offset. */
const AMPSEQ = [0.45, 1, 0.65, 0.25, 0.85, 0.5, 0.95, 0.35] as const;

/** Max lift as a fraction of the seat size (14px on the 104px lobby tile);
 *  the CSS multiplies by --susdle-seat-size so 64px mobile tiles lift ~8.6px. */
export const VOICE_FLOAT_LIFT_RATIO = 0.135;

/** Altitude (normalized) past which wobble is fully unlocked — 6px of the
 *  14px reference lift, mirroring the probe's |y|/6 gate. */
const WOBBLE_GATE = 6 / 14;

/* FNV-1a 32-bit — the same per-player seeding pattern as AvatarFrame's
   paper-grain shift, so a player's wobble identity is as stable as their
   patch of paper. Deterministic across clients by construction (player id). */
function fnv1a(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

interface SeatEntry {
  /** Where the engine writes vars + data-float: the closest
   *  `[data-float-anchor]` (a voting cast cell — so the VoterCluster and
   *  the cell's voted/focus chrome ride the lift with the card) or the tile
   *  itself when no anchor opted in (lobby, clue-giving, curtain call). */
  el: HTMLElement;
  /** 0…2π wobble phase + 0.90…1.10 personal tempo + AMPSEQ offset, all from the id hash. */
  phase: number;
  tempo: number;
  seqIndex: number;
  speaking: boolean;
  /** Envelope state. */
  amp: number;
  ampSlow: number;
  burstTicks: number;
  tickAccMs: number;
  /** ms of silence accumulated since the last speech stop (release window). */
  silenceMs: number;
  /** Spring state — normalized altitude (0…-1) and velocity. */
  y: number;
  v: number;
}

const entries = new Map<HTMLElement, SeatEntry>();
let rafHandle: number | null = null;
let lastT = 0;

function setVars(el: HTMLElement, y: number, ampSlow: number, wx: number, wy: number): void {
  const sw = Math.min(1, Math.abs(y) / WOBBLE_GATE);
  el.style.setProperty('--float-y', y.toFixed(4));
  el.style.setProperty('--float-amp', ampSlow.toFixed(3));
  el.style.setProperty('--float-wx', `${wx.toFixed(2)}deg`);
  el.style.setProperty('--float-wy', `${wy.toFixed(2)}deg`);
  el.style.setProperty('--float-sw', sw.toFixed(3));
}

function clearVars(el: HTMLElement): void {
  el.style.removeProperty('--float-y');
  el.style.removeProperty('--float-amp');
  el.style.removeProperty('--float-wx');
  el.style.removeProperty('--float-wy');
  el.style.removeProperty('--float-sw');
  delete el.dataset['float'];
}

function atRest(e: SeatEntry): boolean {
  return !e.speaking && e.amp === 0 && Math.abs(e.y) < 0.001 && Math.abs(e.v) < 0.005;
}

function frame(t: number): void {
  const dt = Math.min(0.05, (t - lastT) / 1000) || 0.016;
  lastT = t;
  let liveCount = 0;

  for (const e of entries.values()) {
    /* Envelope — stepped ticks while a burst is live; hover floor through the
       release window after it ends; zero once released. */
    if (e.speaking) {
      e.tickAccMs += dt * 1000;
      while (e.tickAccMs >= VOICE_FLOAT_TUNING.tick) {
        e.tickAccMs -= VOICE_FLOAT_TUNING.tick;
        /* Burst-length ramp: a one-word interjection barely stirs (~0.38), a
           held floor reaches full motion after ~1.7s. Burst duration is real
           information; loudness is never available. */
        const ramp = Math.min(1, 0.38 + e.burstTicks * 0.05);
        e.burstTicks++;
        const target = AMPSEQ[e.seqIndex++ % AMPSEQ.length]! * ramp;
        e.amp += (target - e.amp) * VOICE_FLOAT_TUNING.fast;
        e.ampSlow += (e.amp - e.ampSlow) * VOICE_FLOAT_TUNING.slow;
      }
    } else if (e.amp > 0) {
      e.silenceMs += dt * 1000;
      if (e.silenceMs >= VOICE_FLOAT_TUNING.release) {
        e.amp = 0;
        e.ampSlow = 0;
        e.burstTicks = 0;
      }
    }

    /* Damped-spring height integration — runs in both states so the card
       glides home after speech ends, momentum carrying through retargets. */
    const target = -e.amp * VOICE_FLOAT_TUNING.lift;
    const k = VOICE_FLOAT_TUNING.springK;
    const c = 2 * Math.sqrt(k) * VOICE_FLOAT_TUNING.springDamp;
    e.v += (k * (target - e.y) - c * e.v) * dt;
    e.y += e.v * dt;

    if (atRest(e)) {
      if (e.el.dataset['float'] !== undefined) clearVars(e.el);
      continue;
    }

    liveCount++;
    const wx = Math.sin((t / 700) * e.tempo + e.phase) * 4 * VOICE_FLOAT_TUNING.wobble;
    const wy = Math.sin((t / 520) * e.tempo + e.phase * 2) * 5 * VOICE_FLOAT_TUNING.wobble;
    setVars(e.el, e.y, e.ampSlow, wx, wy);
    e.el.dataset['float'] = e.speaking || e.amp > 0 ? 'yes' : 'settling';
  }

  rafHandle = liveCount > 0 ? requestAnimationFrame(frame) : null;
}

function wake(): void {
  if (rafHandle === null) {
    lastT = performance.now();
    rafHandle = requestAnimationFrame(frame);
  }
}

/** Deterministic stills for the dev-mock 'hover' / 'peak' poses — no rAF, no
 *  spring; pure functions of the seed so baselines never flake. */
function setStaticPose(e: SeatEntry, pose: Exclude<SpeakingPose, 'live'>): void {
  const ampSlow = pose === 'peak' ? 0.78 : VOICE_FLOAT_TUNING.hover;
  const y = -ampSlow * VOICE_FLOAT_TUNING.lift;
  const wx = Math.sin(e.phase) * 4 * VOICE_FLOAT_TUNING.wobble;
  const wy = Math.sin(e.phase * 2) * 5 * VOICE_FLOAT_TUNING.wobble;
  setVars(e.el, y, ampSlow, wx, wy);
  e.el.dataset['float'] = 'yes';
}

function register(tileEl: HTMLElement, playerId: string): SeatEntry {
  const h = fnv1a(playerId);
  const entry: SeatEntry = {
    el: tileEl.closest<HTMLElement>('[data-float-anchor]') ?? tileEl,
    phase: (h % 628) / 100,
    tempo: 0.9 + ((h >>> 8) % 21) / 100,
    seqIndex: h % AMPSEQ.length,
    speaking: false,
    amp: 0,
    ampSlow: 0,
    burstTicks: 0,
    tickAccMs: 0,
    silenceMs: 0,
    y: 0,
    v: 0,
  };
  /* Keyed by the tile (the hook's stable ref), not the anchor. */
  entries.set(tileEl, entry);
  return entry;
}

/**
 * Attach the voice float to a tile element. Inert until a SpeakingProvider
 * supplies state. The caller gates eligibility (the float belongs to seat
 * tiles only — never VoterCluster glyphs or the RevealPlate, and never tiles
 * riding a 3D-rotated card face).
 */
export function useVoiceFloat(
  playerId: string,
  eligible: boolean,
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);
  const state = useSpeakingState();
  /* Honors both the OS media query and the in-game Reduce-motion toggle
     (MotionConfig reducedMotion='always'). Under reduced motion the live
     indicator is absent — Discord's own UI already says who is speaking;
     this layer is atmosphere. Static dev-mock poses are stills, not motion,
     so they render regardless (the visual suite runs reducedMotion=reduce). */
  const reduceMotion = useReducedMotion();

  const speaking = state !== null && state.speakingIds.has(playerId);
  const pose: SpeakingPose = state?.pose ?? 'live';
  const active =
    eligible && state !== null && (pose !== 'live' || reduceMotion !== true);

  /* Registration lifecycle. Deliberately NOT keyed on `speaking`: a stop
     event must leave the entry alive so the spring can glide the card home —
     unregistering clears the vars and would snap it to the page. */
  useEffect(() => {
    const el = ref.current;
    if (el === null || !active) return undefined;
    const entry = register(el, playerId);
    return () => {
      entries.delete(el);
      clearVars(entry.el);
    };
  }, [active, playerId]);

  /* State updates onto the registered entry. */
  useEffect(() => {
    const el = ref.current;
    if (el === null || !active) return;
    const entry = entries.get(el);
    if (entry === undefined) return;

    if (pose !== 'live') {
      if (speaking) setStaticPose(entry, pose);
      else clearVars(entry.el);
      return;
    }

    if (entry.speaking !== speaking) {
      entry.speaking = speaking;
      if (speaking) {
        entry.tickAccMs = VOICE_FLOAT_TUNING.tick; // first envelope step lands on the next frame
        entry.silenceMs = 0;
      } else if (entry.amp > 0) {
        /* Hover floor: between closely-spaced bursts the tile dips instead of
           landing; full descent only after the release window expires. */
        entry.amp = VOICE_FLOAT_TUNING.hover;
        entry.ampSlow = VOICE_FLOAT_TUNING.hover;
        entry.burstTicks = 0;
        entry.silenceMs = 0;
      }
    }
    if (speaking || !atRest(entry)) wake();
  }, [active, speaking, pose, playerId]);

  return ref;
}
